import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ArrowUp } from 'lucide-react';

const Footer: React.FC = () => {
  const [showScroll, setShowScroll] = useState(false);

  // ✅ Show the button only after scrolling down
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setShowScroll(true);
      } else {
        setShowScroll(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ✅ Smooth scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <>
      {/* ===== Footer Section ===== */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto py-8 md:py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            
            {/* Hospital Info */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="/RABINISIA LOGO.jpg"
                  alt="Rabininsia Memorial Hospital Logo"
                  className="h-8 w-auto object-contain"
                />
                <h3 className="text-lg font-bold">Rabininsia Memorial Hospital</h3>
              </div>
              <p className="text-sm text-gray-300 mb-4">
                Delivering quality healthcare with compassion and excellence in Dar es Salaam, Tanzania.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-blue-400 flex-shrink-0" />
                  <span className="text-sm">Namanga-Tegeta, Dar es Salaam, Tanzania</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-blue-400 flex-shrink-0" />
                  <span className="text-sm">+255 757 685 894 / +255 658 849 670</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-blue-400 flex-shrink-0" />
                  <a
                    href="mailto:mrmh2003@gmail.com"
                    className="text-sm hover:text-blue-400 transition-colors"
                  >
                    mrmh2003@gmail.com
                  </a>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold mb-3">Follow Us</h4>
                <div className="flex space-x-3">
                  <a
                    aria-label="Facebook"
                    href="https://facebook.com/rabininsiahospital"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 p-2 rounded-full hover:bg-blue-700 transition-colors"
                  >
                    <img src="/icons/facebook.png" alt="Facebook" className="h-4 w-4" />
                  </a>
                  <a
                    aria-label="Instagram"
                    href="https://www.instagram.com/rabininsiamemorial/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-pink-600 p-2 rounded-full hover:bg-pink-700 transition-colors"
                  >
                    <img src="/icons/instagram.png" alt="Instagram" className="h-4 w-4" />
                  </a>
                  <a
                    aria-label="Twitter"
                    href="https://twitter.com/rabininsiahospital"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-400 p-2 rounded-full hover:bg-blue-500 transition-colors"
                  >
                    <img src="/icons/twitter.png" alt="Twitter" className="h-4 w-4" />
                  </a>
                  <a
                    aria-label="TikTok"
                    href="https://tiktok.com/@rabininsiahospital"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-black p-2 rounded-full hover:bg-gray-800 transition-colors"
                  >
                    <img src="/icons/tiktok.png" alt="TikTok" className="h-4 w-4" />
                  </a>
                  <a
                    aria-label="YouTube"
                    href="https://youtube.com/@rabininsiahospital"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-red-600 p-2 rounded-full hover:bg-red-700 transition-colors"
                  >
                    <img src="/icons/youtube.png" alt="YouTube" className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-sm text-gray-300 hover:text-white transition">About Us</Link></li>
                <li><Link to="/services" className="text-sm text-gray-300 hover:text-white transition">Our Services</Link></li>
                <li><Link to="/appointment" className="text-sm text-gray-300 hover:text-white transition">Book Appointment</Link></li>
              </ul>
            </div>

            {/* Visiting Hours */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Visiting Hours</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Clock className="h-4 w-4 text-blue-400" />
                    <span className="font-medium text-sm">Weekdays</span>
                  </div>
                  <div className="text-xs text-gray-300 ml-6">
                    <div>06:00 - 07:00</div>
                    <div>12:30 - 14:00</div>
                    <div>16:00 - 18:00</div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Clock className="h-4 w-4 text-blue-400" />
                    <span className="font-medium text-sm">Weekends/Holidays</span>
                  </div>
                  <div className="text-xs text-gray-300 ml-6">12:00 - 14:00</div>
                </div>
              </div>
            </div>

            {/* Useful Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Useful Links</h4>
              <ul className="space-y-2">
                <li><a href="https://www.moh.go.tz" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-white transition">Ministry of Health</a></li>
                <li><a href="https://www.nhif.or.tz" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-white transition">NHIF</a></li>
                <li><a href="#" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-white transition">IT Department</a></li>
                <li><a href="https://www.tanzania.go.tz" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-white transition">Government Portal</a></li>
                <li><a href="https://www.utumishi.go.tz" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-white transition">Public Service Commission</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 mt-10 pt-6 text-center">
            <p className="text-gray-400 text-sm">
              © 2025 Rabininsia Memorial Hospital. All Rights Reserved.
            </p>
            <p className="text-gray-500 text-xs md:text-sm mt-1">
              Website Designed and Managed by{' '}
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 transition"
              >
                IT Department 
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* ===== Floating Back-to-Top Button (Centered) ===== */}
      {showScroll && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-5 left-1/2 transform -translate-x-1/2 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 z-50"
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
    </>
  );
};

export default Footer;
